import { useState, useRef, useEffect } from 'react';
import { Download, FileSpreadsheet, FileText, FileJson, Loader2 } from 'lucide-react';
import type { ExportFormat } from '@yawi/types';

interface ReportExportButtonProps {
  onExport: (format: ExportFormat) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
}

const FORMAT_OPTIONS: { format: ExportFormat; label: string; icon: typeof FileSpreadsheet }[] = [
  { format: 'excel', label: 'Excel (.xlsx)', icon: FileSpreadsheet },
  { format: 'pdf', label: 'PDF', icon: FileText },
  { format: 'json', label: 'JSON', icon: FileJson },
];

export const ReportExportButton = ({ onExport, loading, disabled }: ReportExportButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (format: ExportFormat) => {
    setExportingFormat(format);
    setIsOpen(false);
    try {
      await onExport(format);
    } finally {
      setExportingFormat(null);
    }
  };

  const isExporting = loading || exportingFormat !== null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isExporting}
        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isExporting ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <Download size={18} />
        )}
        <span>Exportar</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
          {FORMAT_OPTIONS.map(({ format, label, icon: Icon }) => (
            <button
              key={format}
              onClick={() => handleExport(format)}
              className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Icon size={16} className="text-slate-400" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
